/**
 * DocumentUpload — Bulk PDF engineering package upload.
 *
 * Flow:
 *   1. Drop / browse PDFs (multi-select)
 *   2. Filename parser extracts Job + PMK + Rev (with confidence)
 *   3. User reviews/corrects in a mapping table
 *   4. Upload all → backend stores files + creates document records
 *
 * Works standalone (/documents/upload) or embedded in Job Detail via props.
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box, Button, Typography, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Stack, Alert,
  LinearProgress, Select, MenuItem, FormControl, Paper,
  Container, Breadcrumbs, IconButton, TextField, Tooltip,
  Card,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CloudUploadOutlined, CheckCircle,
  ErrorOutline, HomeRounded, DeleteOutline,
  PictureAsPdfOutlined, WarningAmber,
} from "@mui/icons-material";
import { getJobs } from "../../feature/jobSlice";
import ApiCall from "../../utils/apicall";
import parsePdfFilename from "../../utils/pdfFilenameParser";
import { showSuccessToast, showErrorToast } from "../../utils/Toast";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── Styled drop zone ────────────────────────────────────────────────────────

const DropZone = styled(Box)(({ theme, isDragging }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(6),
  textAlign: "center",
  cursor: "pointer",
  background: isDragging ? theme.palette.action.hover : "transparent",
  transition: "all .2s",
}));

// ─── Confidence chip helper ──────────────────────────────────────────────────

function ConfidenceChip({ level }) {
  const map = {
    high:   { label: "High",   color: "success" },
    medium: { label: "Medium", color: "warning" },
    low:    { label: "Low",    color: "error"   },
  };
  const { label, color } = map[level] || map.low;
  return <Chip label={label} color={color} size="small" variant="outlined" />;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function DocumentUpload({ preselectedJobId = null, embedded = false, onUploadComplete = null }) {
  const dispatch = useDispatch();
  const jobs = useSelector((state) => state.jobSlice?.jobs || []);
  const fileInputRef = useRef(null);

  // Codex F3 fix 2026-05-07: when this page is mounted standalone at
  // /documents/upload?job_id=X (e.g. from DetailingWorkspace's "Upload
  // Documents" quick action), read job_id from the query string and use
  // it as a fallback for the preselectedJobId prop. Without this wiring,
  // the page ignored the job_id param and uploads landed unassociated —
  // a real audit-trail risk per AISC 303 §A.4 documented-information
  // control. The prop still wins when both are present (embedded use case).
  const [searchParams] = useSearchParams();
  const jobIdFromQuery = searchParams.get("job_id");
  const effectiveJobId = preselectedJobId
    ?? (jobIdFromQuery ? Number(jobIdFromQuery) || jobIdFromQuery : null);

  // Parsed file list: [{ id, file, job, pmk, rev, confidence, jobId, status, error }]
  const [parsedFiles, setParsedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);

  // CR-2026-FE-DOC-UPLOAD-METADATA 2026-05-05 — Batch defaults panel.
  // 5 attributes captured at upload time per Dean's request:
  //   sheets (count), issued (date), addendas (text),
  //   fab category (SIMPLE/CONVENTIONAL/COMPLEXED), detailer (text).
  // These apply to ALL files in the batch. Persisted as snapshots on each
  // document row so the metadata travels with the file (audit-defensible
  // AISC §1.7.0 §3 — each drawing delivery is provenanced).
  const [batchSheetCount, setBatchSheetCount] = useState("");
  const [batchIssuedAt, setBatchIssuedAt] = useState("");
  const [batchAddendas, setBatchAddendas] = useState("");
  const [batchFabCategory, setBatchFabCategory] = useState("");
  const [batchDetailerName, setBatchDetailerName] = useState("");

  // Piece marks per job (fetched on demand)
  const [pmkCache, setPmkCache] = useState({}); // { jobId: [{ id, name }] }

  useEffect(() => {
    dispatch(getJobs());
  }, [dispatch]);

  // Build a lookup: lowercase job name → job object
  const jobLookup = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      map[(j.name || "").toLowerCase().trim()] = j;
      // Also index by job number if it differs
      if (j.job_number) map[j.job_number.toLowerCase().trim()] = j;
    });
    return map;
  }, [jobs]);

  // Fetch piece marks for a job (cached)
  const fetchPmksForJob = useCallback(async (jobId) => {
    if (!jobId || pmkCache[jobId]) return;
    try {
      const res = await ApiCall.get(`/fabricated-items/get-fabricated-item/${jobId}`);
      const items = res.data?.data?.fabricateditem || [];
      // Deduplicate by name
      const unique = [...new Map(items.map((i) => [i.name, i])).values()];
      setPmkCache((prev) => ({ ...prev, [jobId]: unique }));
    } catch {
      // silent — we'll just show a text field
    }
  }, [pmkCache]);

  // ── File handling ──────────────────────────────────────────────────────────

  const processFiles = useCallback((fileList) => {
    const pdfs = Array.from(fileList).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    if (pdfs.length === 0) {
      showErrorToast("No PDF files detected. Please upload .pdf files only.");
      return;
    }

    const newEntries = pdfs.map((file, idx) => {
      const parsed = parsePdfFilename(file.name);

      // Try to match parsed job name to an existing job. Codex F3 fix:
      // effectiveJobId honours both the embedded `preselectedJobId` prop
      // AND the `?job_id=` query string from DetailingWorkspace.
      let matchedJobId = effectiveJobId || null;
      if (!matchedJobId && parsed.job) {
        const key = parsed.job.toLowerCase().trim();
        const match = jobLookup[key];
        if (match) matchedJobId = match.id;
      }

      return {
        id: `${Date.now()}-${idx}`,
        file,
        fileName: file.name,
        fileSize: file.size,
        job: parsed.job,
        pmk: parsed.pmk,
        rev: parsed.rev || "",
        confidence: parsed.confidence,
        jobId: matchedJobId,
        pmkId: null,       // Will be set by user or auto-match
        status: "pending",  // pending | uploading | success | error
        error: null,
      };
    });

    setParsedFiles((prev) => [...prev, ...newEntries]);

    // Prefetch piece marks for matched jobs
    const jobIds = [...new Set(newEntries.map((e) => e.jobId).filter(Boolean))];
    jobIds.forEach(fetchPmksForJob);
  }, [jobLookup, effectiveJobId, fetchPmksForJob]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const handleFileSelect = useCallback((e) => {
    processFiles(e.target.files);
    e.target.value = "";
  }, [processFiles]);

  const removeFile = (id) => {
    setParsedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearAll = () => {
    setParsedFiles([]);
    setUploadComplete(false);
    setUploadProgress(0);
  };

  // ── Field updates ──────────────────────────────────────────────────────────

  const updateField = (id, field, value) => {
    setParsedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );

    // If job changed, fetch piece marks for it
    if (field === "jobId" && value) {
      fetchPmksForJob(value);
    }
  };

  // ── Upload ─────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    const pending = parsedFiles.filter((f) => f.status !== "success");
    if (pending.length === 0) return;

    // Validate: every file needs a jobId
    const missingJob = pending.filter((f) => !f.jobId);
    if (missingJob.length > 0) {
      showErrorToast(`${missingJob.length} file(s) have no job assigned. Please select a job for each file.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    let successCount = 0;

    for (let i = 0; i < pending.length; i++) {
      const entry = pending[i];
      setParsedFiles((prev) =>
        prev.map((f) => (f.id === entry.id ? { ...f, status: "uploading" } : f))
      );

      try {
        const formData = new FormData();
        formData.append("file", entry.file);
        formData.append("job_id", entry.jobId);
        formData.append("pmk", entry.pmk || "");
        formData.append("revision", entry.rev || "");
        formData.append("doc_type", "engineering_package");
        if (entry.pmkId) formData.append("fabricated_item_id", entry.pmkId);

        // CR-2026-FE-DOC-UPLOAD-METADATA 2026-05-05 — apply batch defaults
        // to every file in this upload. Server stores them as upload-time
        // snapshots on the document row.
        if (batchSheetCount && String(batchSheetCount).trim() !== "") {
          formData.append("sheet_count", String(batchSheetCount).trim());
        }
        if (batchIssuedAt) formData.append("issued_at", batchIssuedAt);
        if (batchAddendas && batchAddendas.trim()) {
          formData.append("addendas", batchAddendas.trim());
        }
        if (batchFabCategory) formData.append("fabrication_category_snapshot", batchFabCategory);
        if (batchDetailerName && batchDetailerName.trim()) {
          formData.append("detailer_name_snapshot", batchDetailerName.trim());
        }

        await ApiCall.post("/document/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setParsedFiles((prev) =>
          prev.map((f) => (f.id === entry.id ? { ...f, status: "success" } : f))
        );
        successCount++;
      } catch (err) {
        const msg = err?.response?.data?.message || "Upload failed";
        setParsedFiles((prev) =>
          prev.map((f) => (f.id === entry.id ? { ...f, status: "error", error: msg } : f))
        );
      }

      setUploadProgress(Math.round(((i + 1) / pending.length) * 100));

      // Small delay between uploads to avoid rate limiting
      if (i < pending.length - 1) {
        await new Promise((r) => setTimeout(r, 200));
      }
    }

    setUploading(false);
    setUploadComplete(true);

    if (successCount === pending.length) {
      showSuccessToast(`All ${successCount} document(s) uploaded and itemized.`);
    } else {
      showErrorToast(`${successCount}/${pending.length} uploaded. Check errors below.`);
    }

    // Notify parent (Job Detail) so it can refresh its Drawings list.
    if (typeof onUploadComplete === "function" && successCount > 0) {
      try { onUploadComplete({ successCount, totalCount: pending.length }); } catch (_) {}
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const pendingCount = parsedFiles.filter((f) => f.status === "pending" || f.status === "error").length;
  const successCount = parsedFiles.filter((f) => f.status === "success").length;

  const content = (
    <>
      {/* Drop zone */}
      {parsedFiles.length === 0 || !uploading ? (
        <DropZone
          isDragging={isDragging}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{ mb: 3 }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf"
            multiple
            hidden
          />
          <CloudUploadOutlined sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Drop PDF files here or click to browse
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Engineering packages, IFC drawings, RFIs, spec sheets — we'll parse
            filenames to match them to Jobs and Piece Marks automatically.
          </Typography>
        </DropZone>
      ) : null}

      {/* CR-2026-FE-DOC-UPLOAD-METADATA 2026-05-05 — Batch defaults panel.
          Captures 5 attributes that apply to all files in this upload batch
          and persist as snapshots on each document row. */}
      {parsedFiles.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.neutral" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: "0.04em" }}>
              BATCH METADATA
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              applies to all files in this upload — captured as upload-time snapshot per AISC §1.7.0 §3
            </Typography>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              size="small"
              label="Sheets"
              type="number"
              value={batchSheetCount}
              onChange={(e) => setBatchSheetCount(e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ minWidth: 100 }}
              placeholder="e.g. 60"
              disabled={uploading}
            />
            <TextField
              size="small"
              label="Issued"
              type="date"
              value={batchIssuedAt}
              onChange={(e) => setBatchIssuedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
              disabled={uploading}
            />
            <TextField
              size="small"
              label="Addendas"
              value={batchAddendas}
              onChange={(e) => setBatchAddendas(e.target.value)}
              placeholder="e.g. ADD-1, ADD-2"
              sx={{ flex: 1, minWidth: 180 }}
              disabled={uploading}
            />
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={uploading}>
              <Select
                value={batchFabCategory}
                onChange={(e) => setBatchFabCategory(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Fabrication Category…</em>
                </MenuItem>
                <MenuItem value="SIMPLE">Simple</MenuItem>
                <MenuItem value="CONVENTIONAL">Conventional</MenuItem>
                <MenuItem value="COMPLEXED">Complexed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Detailer"
              value={batchDetailerName}
              onChange={(e) => setBatchDetailerName(e.target.value)}
              placeholder="Detailer name"
              sx={{ flex: 1, minWidth: 180 }}
              disabled={uploading}
            />
          </Stack>
        </Paper>
      )}

      {/* File count summary */}
      {parsedFiles.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Chip
            icon={<PictureAsPdfOutlined />}
            label={`${parsedFiles.length} file${parsedFiles.length !== 1 ? "s" : ""}`}
            color="primary"
            variant="outlined"
          />
          {successCount > 0 && (
            <Chip icon={<CheckCircle />} label={`${successCount} uploaded`} color="success" size="small" />
          )}
          {pendingCount > 0 && !uploading && (
            <Chip label={`${pendingCount} pending`} color="warning" size="small" variant="outlined" />
          )}
          <Box sx={{ flex: 1 }} />
          {!uploading && (
            <Button size="small" color="inherit" onClick={clearAll}>
              Clear All
            </Button>
          )}
          {!uploading && parsedFiles.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() => fileInputRef.current?.click()}
            >
              + Add More
            </Button>
          )}
        </Stack>
      )}

      {/* Upload progress */}
      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Mapping table */}
      {parsedFiles.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 500 }}>
          <SmartTable tableId="document-upload" lockedColumns={["Filename", "Rev", "Status"]} size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Filename</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Job</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Piece Mark</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 90 }}>Rev</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 90 }}>Match</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 90 }}>Status</TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedFiles.map((entry, idx) => (
                <TableRow
                  key={entry.id}
                  sx={{
                    backgroundColor:
                      entry.status === "success" ? "rgba(76,175,80,0.06)" :
                      entry.status === "error"   ? "rgba(244,67,54,0.06)" :
                      "inherit",
                  }}
                >
                  <TableCell>{idx + 1}</TableCell>

                  {/* Filename */}
                  <TableCell>
                    <Tooltip title={entry.fileName}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {entry.fileName}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {(entry.fileSize / 1024).toFixed(0)} KB
                    </Typography>
                  </TableCell>

                  {/* Job selector */}
                  <TableCell>
                    <FormControl size="small" fullWidth disabled={entry.status === "success" || uploading}>
                      <Select
                        value={entry.jobId || ""}
                        onChange={(e) => updateField(entry.id, "jobId", e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          <em>Select Job</em>
                        </MenuItem>
                        {jobs.map((j) => (
                          <MenuItem key={j.id} value={j.id}>
                            {j.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  {/* Piece Mark — text field or dropdown if PMKs loaded */}
                  <TableCell>
                    {entry.jobId && pmkCache[entry.jobId]?.length > 0 ? (
                      <FormControl size="small" fullWidth disabled={entry.status === "success" || uploading}>
                        <Select
                          value={entry.pmkId || "custom"}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "custom") {
                              updateField(entry.id, "pmkId", null);
                            } else {
                              const item = pmkCache[entry.jobId].find((p) => p.id === val);
                              updateField(entry.id, "pmkId", val);
                              if (item) updateField(entry.id, "pmk", item.name);
                            }
                          }}
                          displayEmpty
                        >
                          <MenuItem value="custom">
                            <em>{entry.pmk || "Custom..."}</em>
                          </MenuItem>
                          {pmkCache[entry.jobId].map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        value={entry.pmk || ""}
                        onChange={(e) => updateField(entry.id, "pmk", e.target.value)}
                        disabled={entry.status === "success" || uploading}
                        placeholder="e.g. 1GC1"
                      />
                    )}
                  </TableCell>

                  {/* Revision */}
                  <TableCell>
                    <TextField
                      size="small"
                      value={entry.rev || ""}
                      onChange={(e) => updateField(entry.id, "rev", e.target.value)}
                      disabled={entry.status === "success" || uploading}
                      placeholder="A"
                      sx={{ width: 70 }}
                    />
                  </TableCell>

                  {/* Confidence */}
                  <TableCell>
                    <ConfidenceChip level={entry.confidence} />
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    {entry.status === "success" && <CheckCircle color="success" fontSize="small" />}
                    {entry.status === "error" && (
                      <Tooltip title={entry.error || "Failed"}>
                        <ErrorOutline color="error" fontSize="small" />
                      </Tooltip>
                    )}
                    {entry.status === "uploading" && (
                      <LinearProgress sx={{ width: 40 }} />
                    )}
                    {entry.status === "pending" && (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>

                  {/* Remove */}
                  <TableCell>
                    {entry.status !== "success" && !uploading && (
                      <IconButton size="small" onClick={() => removeFile(entry.id)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      {/* Low-confidence warning */}
      {parsedFiles.some((f) => f.confidence === "low" && f.status === "pending") && (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
          Some filenames couldn't be parsed reliably (Low confidence). Please
          verify the Job and Piece Mark columns before uploading.
        </Alert>
      )}

      {/* Upload button */}
      {parsedFiles.length > 0 && pendingCount > 0 && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={<CloudUploadOutlined />}
          >
            Upload {pendingCount} Document{pendingCount !== 1 ? "s" : ""}
          </Button>
        </Stack>
      )}

      {/* Done summary */}
      {uploadComplete && pendingCount === 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          All documents uploaded and itemized successfully. They're now linked to
          their respective Jobs and Piece Marks.
        </Alert>
      )}
    </>
  );

  // If embedded in Job Detail, render without page wrapper
  if (embedded) {
    return <Box sx={{ mt: 2 }}>{content}</Box>;
  }

  return (
    <>
      <Helmet>
        <title>Document Upload | SFM</title>
      </Helmet>
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={4}
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Stack direction="row" alignItems="center" spacing={1}>
              <HomeRounded color="inherit" />
              <Typography variant="body1" color="textPrimary">
                / Documents / Upload
              </Typography>
            </Stack>
          </Breadcrumbs>
        </Stack>

        <Card sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            Bulk Document Upload
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload engineering packages, IFC drawings, and shop drawings.
            Filenames are parsed automatically to match Jobs and Piece Marks —
            review the table below before confirming.
          </Typography>
          {content}
        </Card>
      </Container>
    </>
  );
}
